import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrder } from '../../context/OrderContext';
import { Header } from '../../layout';
import { AlbaSubCategorySelector, AlbaProductSelector } from '../../selectors/Alba';
import RALModal from '../../modals/RALModal';
import { validateRal, getAutoTierCode, getAutoTierPrice } from '../../utils/albaFlowRules';
import Swal from 'sweetalert2';

const TREND_COLORS = [
  { ral: '9016', label: 'RAL 9016 — Bianco Opaco' },
  { ral: '9005', label: 'RAL 9005 — Nero Opaco' },
];

const AlbaOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useOrder();

  const initialSubCategory = location.state?.subCategory === 'accessori' ? 'accessori' : null;
  const [step, setStep] = useState(initialSubCategory ? 'product' : 'subcategory');
  const [subCategory, setSubCategory] = useState(initialSubCategory);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [ralCode, setRalCode] = useState('');
  const [showRalModal, setShowRalModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [wantFixing, setWantFixing] = useState(false);

  const handleBack = () => {
    if (step === 'subcategory') {
      navigate('/product-categories');
    } else if (step === 'product') {
      setStep('subcategory');
    } else if (step === 'trendColor') {
      setStep('product');
    } else if (step === 'quantity') {
      setQuantity('');
      setRalCode('');
      if (selectedProduct?.ralRestriction === 'trend') {
        setStep('trendColor');
      } else {
        setStep('product');
      }
    }
  };

  const handleSubCategorySelect = (id) => {
    setSubCategory(id);
    setSelectedProduct(null);
    setQuantity('');
    setRalCode('');
    setWantFixing(false);
    setStep('product');
  };

  const handleProductSelect = (product) => {
    if (product.ralRestriction === 'trend') {
      // Trend: solo 2 colori, mostra step scelta colore
      setPendingProduct(product);
      setStep('trendColor');
    } else if (product.needsRal) {
      // Custom RAL o Magic SPE: apri modal RAL
      setPendingProduct(product);
      setShowRalModal(true);
    } else {
      // Standard: vai diretto a quantità
      setSelectedProduct(product);
      setRalCode('');
      setStep('quantity');
    }
  };

  const handleTrendColorSelect = (ral) => {
    setRalCode(ral);
    setSelectedProduct(pendingProduct);
    setPendingProduct(null);
    setStep('quantity');
  };

  const handleRalConfirm = (formattedRal) => {
    const code = formattedRal.replace('RAL ', '');
    const err = validateRal(code, pendingProduct?.ralRestriction, subCategory);
    if (err) {
      Swal.fire({ icon: 'error', title: 'RAL non valido', text: err, confirmButtonColor: '#cd212a' });
      return;
    }
    setRalCode(code);
    setSelectedProduct(pendingProduct);
    setPendingProduct(null);
    setShowRalModal(false);
    setStep('quantity');
  };

  const isMagicCustom = subCategory === 'magic' && selectedProduct?.needsRal;
  const getMinQty = () => {
    if (isMagicCustom) return 20;
    return selectedProduct?.minQty || 1;
  };

  const getEffectiveProduct = () => {
    if (!isMagicCustom) return selectedProduct;
    const qty = parseInt(quantity, 10) || 0;
    const tierCode = getAutoTierCode(qty, subCategory);
    const tierPrice = getAutoTierPrice(qty);
    if (tierCode && tierPrice) return { ...selectedProduct, code: tierCode, price: tierPrice };
    return selectedProduct;
  };

  const handleAddToCart = () => {
    const product = getEffectiveProduct();
    const qty = parseInt(quantity, 10);
    const minQty = getMinQty();
    if (!product || !qty || qty < minQty) return;

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        productCode: product.code,
        quantity: qty,
        price: product.price,
        category: 'alba',
        albaSubCategory: subCategory,
        ralCode: ralCode || null,
        description: product.finish || product.description,
        isAlbaItem: true,
        isStandaloneArticle: true,
      },
    });

    // Add zinc fixing (M13-AC-08) if requested for Circle/Square
    if (wantFixing && (subCategory === 'circle' || subCategory === 'square')) {
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          productCode: 'M13-AC-08',
          quantity: qty,
          price: 4.00,
          category: 'alba',
          albaSubCategory: 'accessori',
          description: 'Astina zincata M12x140 con dado e rondella',
          isAlbaItem: true,
          isStandaloneArticle: true,
          isAutomaticAddition: true,
        },
      });
    }

    Swal.fire({
      title: 'Aggiunto al carrello',
      html: `<strong>${product.code}</strong> &times; ${qty}${ralCode ? ` (RAL ${ralCode})` : ''}`,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Vai al carrello',
      cancelButtonText: 'Aggiungi altro',
      confirmButtonColor: '#cd212a',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/order-summary');
      } else {
        setStep('subcategory');
        setSubCategory(null);
        setSelectedProduct(null);
        setQuantity('');
        setRalCode('');
      }
    });
  };

  const qtyNum = parseInt(quantity, 10) || 0;
  const minQty = getMinQty();
  const qtyError = quantity && qtyNum > 0 && qtyNum < minQty ? `Quantità minima: ${minQty} pezzi` : null;
  const effectiveProduct = getEffectiveProduct();

  const getRalModalSubtitle = () => {
    if (pendingProduct?.ralRestriction === 'custom') {
      return 'Inserisci qualsiasi codice RAL opaco (esclusi 9016 e 9005 — disponibili come Trend)';
    }
    if (subCategory === 'magic') {
      return 'Inserisci un codice RAL opaco (esclusi i colori standard: 9016, 7016, NKR3, 1036)';
    }
    return 'Inserisci il codice RAL opaco desiderato';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title={location.state?.type === 'offerta' ? 'Richiesta Offerta' : 'Nuovo Ordine'}
        backText="Categoria"
        backTo="/product-categories"
      />
      <div className="container mx-auto px-4 py-6">
        {step !== 'subcategory' && (
          <button onClick={handleBack} className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Indietro
          </button>
        )}

        {step === 'subcategory' && (
          <AlbaSubCategorySelector onSelect={handleSubCategorySelect} />
        )}

        {step === 'product' && (
          <AlbaProductSelector subCategory={subCategory} onSelect={handleProductSelect} />
        )}

        {/* Trend color selection — solo 2 opzioni, selezione diretta */}
        {step === 'trendColor' && pendingProduct && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">SELEZIONA COLORE TREND</h1>
            <p className="mb-4 text-gray-600">{pendingProduct.code} — Solo 2 colori disponibili</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-w-lg mx-auto">
              {TREND_COLORS.map(color => (
                <button
                  key={color.ral}
                  onClick={() => handleTrendColorSelect(color.ral)}
                  className="p-4 border rounded-lg border-gray-300 hover:bg-red-100 hover:border-red-500 transition-colors duration-200"
                >
                  <div className="flex flex-col items-center">
                    <div
                      className="w-16 h-16 rounded-full mb-2 border border-gray-300"
                      style={{ backgroundColor: color.ral === '9016' ? '#F7F5F0' : '#0A0A0A' }}
                    />
                    <span className="font-medium">{color.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'quantity' && selectedProduct && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">QUANTITÀ</h1>
            <p className="mb-4 text-gray-600">
              {selectedProduct.code} — {selectedProduct.finish || selectedProduct.description}
              {ralCode && <span className="ml-2 text-red-600 font-medium">RAL {ralCode}</span>}
            </p>

            <div className="max-w-md mx-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">QUANTITÀ (pz)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min={minQty}
                  autoFocus
                  className={`w-full p-3 text-base border-2 rounded-lg focus:ring-1 focus:ring-red-500 outline-none ${
                    qtyError ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
                  }`}
                  placeholder={`Inserisci la quantità${minQty > 1 ? ` (min. ${minQty})` : ''}`}
                />
                {qtyError && <p className="text-sm text-red-600 bg-red-50 p-2 rounded mt-2">{qtyError}</p>}
                {isMagicCustom && qtyNum >= 20 && (
                  <p className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                    Fascia: {qtyNum <= 50 ? 'SPE1' : qtyNum <= 200 ? 'SPE2' : 'SPE3'} — &euro; {effectiveProduct?.price.toFixed(2)}/pz
                  </p>
                )}
                {!qtyError && qtyNum > 0 && effectiveProduct && (
                  <p className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                    Totale: {qtyNum} &times; &euro; {effectiveProduct.price.toFixed(2)} = <strong>&euro; {(qtyNum * effectiveProduct.price).toFixed(2)}</strong>
                  </p>
                )}
              </div>

              {(subCategory === 'circle' || subCategory === 'square') && (
                <div className="pt-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={wantFixing}
                      onChange={(e) => setWantFixing(e.target.checked)}
                      className="w-5 h-5 text-red-500 rounded border-gray-300 focus:ring-red-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Aggiungi fissaggio zincato</span>
                      <p className="text-xs text-gray-500">M13-AC-08 — Astina zincata M12x140 (€ 4,00/pz) — stessa quantità delle borchie</p>
                    </div>
                  </label>
                </div>
              )}

              <div className="pt-4">
                <button
                  onClick={handleAddToCart}
                  disabled={!qtyNum || qtyNum < minQty}
                  className="w-full py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  Aggiungi al carrello
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <RALModal
        isOpen={showRalModal}
        onClose={() => { setShowRalModal(false); setPendingProduct(null); }}
        onConfirm={handleRalConfirm}
        finishType={pendingProduct?.code?.includes('SPE') ? 'SPE' : 'V'}
      />
    </div>
  );
};

export default AlbaOrderForm;
